import { Link } from "react-router-dom";
import { ArrowRight, Check, Info } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

function Section({ title, children }) {
  return (
    <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {title && <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight mb-5">{title}</h2>}
      {children}
    </section>
  );
}

export function SeoSections({ intro, howTo, requirements, faq, links, accent = "blue" }) {
  const accentText = accent === "indigo" ? "text-indigo-600" : "text-blue-600";
  return (
    <div className="bg-white border-t border-slate-200">
      {intro && intro.length > 0 && (
        <Section>
          <div className="space-y-4 text-base text-slate-600 leading-relaxed">
            {intro.map((p, i) => <p key={i}>{p}</p>)}
          </div>
        </Section>
      )}

      {howTo && howTo.length > 0 && (
        <Section title="How to use it">
          <ol className="space-y-4">
            {howTo.map((s, i) => (
              <li key={i} className="flex items-start gap-4">
                <span className="w-8 h-8 rounded-lg brand-gradient text-white font-bold flex items-center justify-center shrink-0 text-sm">{i + 1}</span>
                <p className="text-base text-slate-600 pt-1">{s}</p>
              </li>
            ))}
          </ol>
        </Section>
      )}

      {requirements && (
        <div className="bg-slate-50 border-y border-slate-200">
          <Section title={requirements.title}>
            <ul className="space-y-3">
              {requirements.items.map((it, i) => (
                <li key={i} className="flex items-start gap-3 bg-white rounded-xl p-4 border border-slate-100">
                  <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center shrink-0 mt-0.5"><Check className="w-3.5 h-3.5" /></span>
                  <span className="text-sm text-slate-700">{it}</span>
                </li>
              ))}
            </ul>
          </Section>
        </div>
      )}

      {faq && faq.length > 0 && (
        <Section title="Frequently asked questions">
          <Accordion type="single" collapsible className="w-full">
            {faq.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-left text-slate-900 font-semibold">{f.q}</AccordionTrigger>
                <AccordionContent className="text-slate-600 text-base leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Section>
      )}

      {links && links.length > 0 && (
        <Section title="Related tools & guides">
          <div className="grid sm:grid-cols-2 gap-4">
            {links.map((r) => (
              <Link key={r.path} to={r.path} data-testid={`related-${r.path}`} className="group flex items-center justify-between bg-white border border-slate-200 hover:border-blue-400 rounded-xl p-5 transition-colors">
                <span className="font-semibold text-slate-800 group-hover:text-blue-700">{r.label}</span>
                <ArrowRight className={`w-5 h-5 text-slate-400 group-hover:${accentText} group-hover:translate-x-1 transition-transform`} />
              </Link>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}

export function InfoNote({ children }) {
  return (
    <div className="flex items-start gap-3 bg-indigo-50 border border-indigo-200 rounded-2xl p-5 max-w-4xl mx-auto">
      <Info className="w-5 h-5 text-indigo-600 shrink-0 mt-0.5" />
      <p className="text-sm text-slate-700 leading-relaxed">{children}</p>
    </div>
  );
}
